/**
 * Inline copy canonicalization — metric-rich, confidence-aware text for dashboard surfaces.
 * Mirrors the rigor of notification-templates.ts but for inline UI copy.
 * All inline text on the dashboard should route through this module.
 *
 * Rules (from NOTIFICATION_CANON.md):
 * - Every message answers: What happened? Why? What are the numbers? How confident?
 * - No vague language ("elevated", "may be limited", "monitor closely")
 * - Metrics with units (kWh, kW, hours)
 * - Confidence only on forecasts/predictions, never observed facts
 */

export interface AssessmentContext {
  energyRemainingKwh: number;
  usableKwh: number;
  reserveKwh: number;
  worstCaseKwh?: number;
  timeToSunrise: string;
  confidence?: 'low' | 'medium' | 'high';
}

export interface ReadinessContext {
  /** Minutes since last successful data fetch */
  dataAgeMinutes?: number;
  /** Estimated days until baselines are ready */
  daysToBaseline?: number;
}

export interface GuidanceOverrideContext {
  energyRemainingKwh?: number;
  reserveKwh?: number;
  timeToReserve?: string;
  confidence?: 'low' | 'medium' | 'high';
}

export interface ResilienceContext {
  forecastKwh?: number;
  loadKw?: number;
  runtimeHours?: number;
  confidence?: 'low' | 'medium' | 'high';
}

// --- Assessment copy (EnergyPosition callout) ---

export function assessmentCopy(
  state: 'healthy' | 'watch' | 'critical',
  ctx: AssessmentContext
): { label: string; message: string } {
  const fmtKwh = (v: number) => v.toFixed(1);

  switch (state) {
    case 'healthy':
      return {
        label: 'All Clear',
        message: `${fmtKwh(ctx.usableKwh)} kWh usable above reserve. No action needed.`,
      };
    case 'watch':
      return {
        label: 'Watch',
        message: ctx.worstCaseKwh != null
          ? `Worst-case forecast: ${fmtKwh(ctx.worstCaseKwh)} kWh at sunrise (reserve: ${fmtKwh(ctx.reserveKwh)} kWh). ${ctx.confidence ? `${capitalize(ctx.confidence)} confidence.` : ''} Reduce non-essential load if possible.`
          : `${fmtKwh(ctx.usableKwh)} kWh usable, ${ctx.timeToSunrise} to sunrise. Reserve: ${fmtKwh(ctx.reserveKwh)} kWh. Reduce non-essential load if possible.`,
      };
    case 'critical':
      return {
        label: 'Action Needed',
        message: ctx.worstCaseKwh != null
          ? `Reserve breach projected. Worst-case: ${fmtKwh(ctx.worstCaseKwh)} kWh at sunrise (reserve: ${fmtKwh(ctx.reserveKwh)} kWh). ${ctx.confidence ? `${capitalize(ctx.confidence)} confidence.` : ''} Reduce load or start generator.`
          : `${fmtKwh(ctx.usableKwh)} kWh usable with ${ctx.timeToSunrise} to sunrise. Reserve breach projected. Reduce load or start generator.`,
      };
  }
}

// --- Readiness banner copy (PropertyDashboard) ---

export function readinessCopy(
  state: 'provisional' | 'connecting' | 'warming_up' | 'learning' | 'degraded',
  ctx?: ReadinessContext
): { message: string; severity: 'info' | 'warning' | 'error' } {
  switch (state) {
    case 'provisional':
      return {
        message: 'This property is set up but not yet connected to a monitoring source. Connect a gateway or add cloud credentials to start receiving data.',
        severity: 'info',
      };
    case 'connecting':
      return {
        message: 'Connecting to your system. Data will appear shortly.',
        severity: 'info',
      };
    case 'warming_up':
      return {
        message: ctx?.daysToBaseline
          ? `Collecting baseline data (approximately ${ctx.daysToBaseline} days remaining). Forecasts are preliminary.`
          : 'Collecting baseline data. Forecasts are preliminary until baselines are established.',
        severity: 'info',
      };
    case 'learning':
      return {
        message: ctx?.daysToBaseline
          ? `Learning system patterns (approximately ${ctx.daysToBaseline} days remaining). Forecast confidence is capped at medium.`
          : 'Learning system patterns. Forecast confidence is capped at medium until baselines are complete.',
        severity: 'warning',
      };
    case 'degraded': {
      const ageStr = ctx?.dataAgeMinutes != null
        ? ctx.dataAgeMinutes >= 60
          ? `${Math.floor(ctx.dataAgeMinutes / 60)}h ${ctx.dataAgeMinutes % 60}m`
          : `${ctx.dataAgeMinutes}m`
        : null;
      return {
        message: ageStr
          ? `Data is ${ageStr} old. Check system connection or refresh.`
          : 'Data may be outdated. Check system connection or refresh.',
        severity: 'error',
      };
    }
  }
}

// --- Guidance override copy (EnergyGuidanceCard when action_required/watch) ---

export function guidanceOverrideCopy(
  riskState: 'action_required' | 'watch',
  ctx?: GuidanceOverrideContext
): string {
  const fmtKwh = (v: number) => v.toFixed(1);

  if (riskState === 'action_required') {
    const parts: string[] = ['Reserve breach projected.'];
    if (ctx?.energyRemainingKwh != null) {
      parts.push(`${fmtKwh(ctx.energyRemainingKwh)} kWh remaining.`);
    }
    if (ctx?.timeToReserve) {
      parts.push(`Estimated time to reserve: ${ctx.timeToReserve}.`);
    }
    if (ctx?.confidence) {
      parts.push(`${capitalize(ctx.confidence)} confidence.`);
    }
    parts.push('Reduce load or start generator.');
    return parts.join(' ');
  }

  // watch
  const parts: string[] = ['Overnight risk elevated.'];
  if (ctx?.energyRemainingKwh != null && ctx?.reserveKwh != null) {
    parts.push(`${fmtKwh(ctx.energyRemainingKwh)} kWh remaining, reserve at ${fmtKwh(ctx.reserveKwh)} kWh.`);
  }
  if (ctx?.confidence) {
    parts.push(`${capitalize(ctx.confidence)} confidence.`);
  }
  parts.push('Reduce non-essential load if possible.');
  return parts.join(' ');
}

// --- Resilience risk banner copy (OvernightForecastCard) ---

export function resilienceCopy(
  severity: 'warning' | 'critical',
  ctx?: ResilienceContext
): string {
  const parts: string[] = ['Resilience Risk'];
  if (severity === 'critical') {
    parts[0] = 'Resilience Risk — Critical';
  }
  const details: string[] = [];
  if (ctx?.forecastKwh != null) {
    details.push(`forecast: ${ctx.forecastKwh.toFixed(1)} kWh at sunrise`);
  }
  if (ctx?.runtimeHours != null) {
    details.push(`estimated runtime: ${ctx.runtimeHours.toFixed(1)}h`);
  }
  if (ctx?.loadKw != null) {
    details.push(`current load: ${ctx.loadKw.toFixed(1)} kW`);
  }
  if (details.length > 0) {
    parts.push(`— ${details.join(', ')}.`);
  } else {
    parts.push('— solar recovery forecast insufficient for current load.');
  }
  if (ctx?.confidence) {
    parts.push(`${capitalize(ctx.confidence)} confidence.`);
  }
  return parts.join(' ');
}

// --- Poll status copy ---

export function pollStatusOfflineCopy(
  isHomeowner: boolean,
  dataAgeMinutes?: number
): string {
  const ageStr = dataAgeMinutes != null
    ? dataAgeMinutes >= 60
      ? ` for ${Math.floor(dataAgeMinutes / 60)}h ${dataAgeMinutes % 60}m`
      : ` for ${dataAgeMinutes}m`
    : '';
  if (isHomeowner) {
    return `Not connected${ageStr}. Check system power and internet connection.`;
  }
  return `Offline${ageStr}. Check inverter connectivity.`;
}

// --- Helpers ---

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
